import { useParams, useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, CheckCircle, Clock, XCircle, AlertCircle, Phone, Mail, MapPin, Hash } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/apiClient';

interface AgencyDetail {
  id: number;
  name: string;
  abn: string;
  abnEntityName?: string;
  address?: string;
  contactName?: string;
  contactEmail: string;
  contactPhone?: string;
  status: string;
  currentLeaseCount: number;
  subscriptionPlanId?: number;
  parentAgencyId?: number;
  createdAt: string;
  subscription?: {
    planName: string;
    leaseCount: number;
    pricePerLease: number;
    monthlyCost: number;
  };
}

function StatusBadge({ status }: { status: string }) {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1"><CheckCircle className="w-4 h-4" />Active</span>;
    case 'PENDING':
    case 'PENDING_EMAIL_VERIFICATION':
      return <span className="px-3 py-1 text-sm font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1"><Clock className="w-4 h-4" />Pending</span>;
    case 'SUSPENDED':
      return <span className="px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full flex items-center gap-1"><XCircle className="w-4 h-4" />Suspended</span>;
    default:
      return <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
  }
}

export default function AdminAgencyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: agency, isLoading, isError } = useQuery<AgencyDetail>({
    queryKey: ['admin', 'agency', id],
    queryFn: () => api.get<AgencyDetail>(`/admin/agencies/${id}`),
    enabled: !!id,
  });

  const updateStatus = useMutation({
    mutationFn: (status: string) => api.put(`/admin/agencies/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agency', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !agency) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">Agency not found or failed to load.</div>
        </div>
        <button onClick={() => navigate('/admin/agencies')} className="mt-4 flex items-center gap-2 text-sm text-primary hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Agencies
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate('/admin/agencies')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Agencies
      </button>

      <PageHeader
        title={agency.name}
        subtitle={agency.abnEntityName ?? ''}
        icon={<Building2 className="w-8 h-8" />}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {/* Status card */}
        <div className="bg-white rounded-lg shadow p-5 flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Status</h3>
          <StatusBadge status={agency.status} />
          <div className="mt-2">
            <label className="text-xs text-gray-500 block mb-1">Change status</label>
            <select
              defaultValue={agency.status}
              onChange={e => updateStatus.mutate(e.target.value)}
              disabled={updateStatus.isPending}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>
        </div>

        {/* Subscription card */}
        <div className="bg-white rounded-lg shadow p-5 flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Subscription</h3>
          {agency.subscription ? (
            <>
              <p className="text-lg font-semibold text-gray-900">{agency.subscription.planName}</p>
              <p className="text-sm text-gray-600">{agency.subscription.leaseCount} active leases</p>
              <p className="text-sm text-gray-600">${agency.subscription.pricePerLease}/lease/mo</p>
              <p className="text-base font-medium text-primary">${agency.subscription.monthlyCost?.toFixed(2)}/mo</p>
            </>
          ) : (
            <p className="text-sm text-gray-400">No subscription info</p>
          )}
        </div>

        {/* Stats card */}
        <div className="bg-white rounded-lg shadow p-5 flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Stats</h3>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Active leases</span>
            <span className="font-medium text-gray-900">{agency.currentLeaseCount ?? 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Agency ID</span>
            <span className="font-medium text-gray-900">#{agency.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Registered</span>
            <span className="font-medium text-gray-900">{agency.createdAt ? new Date(agency.createdAt).toLocaleDateString() : '—'}</span>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Contact &amp; Details</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {agency.contactName && (
            <div className="flex items-start gap-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-xs text-gray-500">Contact name</dt>
                <dd className="text-sm font-medium text-gray-900">{agency.contactName}</dd>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <dt className="text-xs text-gray-500">Email</dt>
              <dd className="text-sm font-medium text-gray-900">{agency.contactEmail}</dd>
            </div>
          </div>
          {agency.contactPhone && (
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-xs text-gray-500">Phone</dt>
                <dd className="text-sm font-medium text-gray-900">{agency.contactPhone}</dd>
              </div>
            </div>
          )}
          {agency.address && (
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-xs text-gray-500">Address</dt>
                <dd className="text-sm font-medium text-gray-900">{agency.address}</dd>
              </div>
            </div>
          )}
          {agency.abn && (
            <div className="flex items-start gap-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-xs text-gray-500">ABN</dt>
                <dd className="text-sm font-medium text-gray-900">{agency.abn}</dd>
              </div>
            </div>
          )}
          {agency.abnEntityName && (
            <div className="flex items-start gap-3">
              <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-xs text-gray-500">ABN Entity</dt>
                <dd className="text-sm font-medium text-gray-900">{agency.abnEntityName}</dd>
              </div>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
